import type { SFTPConnection, SftpFolderMount } from "types/interfaces/sftp";

export type FolderMountRow = SftpFolderMount & { id: string };

export function newFolderMountRow(): FolderMountRow {
  return { id: crypto.randomUUID(), label: "", prefix: "", subfolders: [] };
}

export function folderMountRowsFromConnection(
  c: SFTPConnection | null,
): FolderMountRow[] {
  if (!c) return [newFolderMountRow()];
  const fm = c.folder_mounts;
  if (fm && fm.length > 0) {
    return fm.map((m) => ({
      id: crypto.randomUUID(),
      label: m.label ?? "",
      prefix: m.prefix ?? "",
      subfolders: [...(m.subfolders ?? [])],
    }));
  }
  return [
    {
      id: crypto.randomUUID(),
      label: "",
      prefix: c.folder ?? "",
      subfolders: [...(c.subfolders ?? [])],
    },
  ];
}

export function folderMountsToPayload(
  rows: FolderMountRow[],
): SftpFolderMount[] {
  return rows.map(({ label, prefix, subfolders }) => ({
    label: label.trim(),
    prefix: prefix.trim().replace(/^\/+/, ""),
    subfolders: [...subfolders],
  }));
}

export function comparableFolderMountsFromConnection(
  c: SFTPConnection,
): string {
  return JSON.stringify(
    folderMountsToPayload(folderMountRowsFromConnection(c)),
  );
}

/** One row in the SFTP table “Bucket / Folder” column. */
export type BucketFolderDisplayLine = {
  sftpLabel: string;
  path: string;
  subfoldersSummary: string | null;
};

export function bucketFolderDisplayLines(
  conn: SFTPConnection,
): BucketFolderDisplayLine[] {
  const bucket = conn.bucket ?? "";
  const mounts = conn.folder_mounts;
  if (mounts && mounts.length > 0) {
    return mounts.map((m) => {
      const prefix = (m.prefix ?? "").trim().replace(/^\/+/, "");
      const path = prefix ? `${bucket}/${prefix}` : bucket;
      const subs = (m.subfolders ?? [])
        .map((s) => String(s).trim())
        .filter(Boolean);
      return {
        sftpLabel: (m.label ?? "").trim(),
        path,
        subfoldersSummary: subs.length > 0 ? subs.join(", ") : null,
      };
    });
  }
  const folder = (conn.folder ?? "").trim().replace(/^\/+/, "");
  const path = folder ? `${bucket}/${folder}` : bucket;
  const subs = (conn.subfolders ?? [])
    .map((s) => String(s).trim())
    .filter(Boolean);
  return [
    {
      sftpLabel: "",
      path,
      subfoldersSummary: subs.length > 0 ? subs.join(", ") : null,
    },
  ];
}

/** Stable lowercase string for sorting the Bucket / Folder column. */
export function bucketFolderSortKey(conn: SFTPConnection): string {
  return bucketFolderDisplayLines(conn)
    .map((L) => `${L.sftpLabel}\x00${L.path}\x00${L.subfoldersSummary ?? ""}`)
    .join("\n")
    .toLowerCase();
}
